/** Per-record outcome of a completed AI import: links to the created
 *  records, skip count, and per-record failures. Rendered inside the task
 *  details dialog. */
import {
  Alert,
  Box,
  Link,
  Stack,
  Typography,
} from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { Link as RouterLink } from 'react-router-dom';

import type { AiImportResult } from '@/api/aiImport';
import * as bgtasksApi from '@/api/bgtasks';

const RECORD_ROUTES: Record<string, string> = {
  tour: '/tours',
  itinerary: '/itineraries',
  activity: '/activities',
};

export function AiImportResultsPanel({ taskId }: { taskId: string }) {
  const query = useQuery({
    queryKey: ['bgtask', taskId, 'result'],
    queryFn: () => bgtasksApi.result(taskId),
    staleTime: Infinity,
    retry: false,
  });
  const result = query.data?.result as unknown as AiImportResult | undefined;
  if (!result || !Array.isArray(result.imported)) return null;

  return (
    <Box>
      <Typography variant="subtitle2" gutterBottom>
        Import result{result.dry_run ? ' (dry run)' : ''}
      </Typography>
      <Stack spacing={0.5}>
        <Typography variant="body2">
          {result.imported.length} imported · {result.skipped} skipped ·{' '}
          {result.failed.length} failed · cost ${result.cost_usd?.toFixed?.(2) ?? '—'}
        </Typography>
        {result.imported.map((entry) => (
          <Typography key={entry.ext_id} variant="body2">
            {RECORD_ROUTES[entry.kind] ? (
              <Link component={RouterLink} to={`${RECORD_ROUTES[entry.kind]}/${entry.record_id}`}>
                {entry.name || entry.ext_id}
              </Link>
            ) : (
              entry.name || entry.ext_id
            )}
            {' '}
            <Typography component="span" variant="caption" color="text.secondary">
              ({entry.kind})
            </Typography>
          </Typography>
        ))}
        {result.failed.length > 0 && (
          <Alert severity="warning" sx={{ mt: 1 }}>
            {result.failed.map((f) => (
              <div key={f.ext_id}>
                <code>{f.ext_id}</code> ({f.kind}): {f.error}
              </div>
            ))}
          </Alert>
        )}
      </Stack>
    </Box>
  );
}
